import { useState, useRef, useEffect } from 'react';
import { Phone, ArrowLeft, User, AtSign, Loader2, Check, ChevronDown } from 'lucide-react';
import { authService } from '@services/authService';
import { useAuthStore } from '@store/authStore';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import CountryCodeSelector from '@components/common/CountryCodeSelector';

interface PhoneLoginFormProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
}

type Step = 'phone' | 'otp' | 'name' | 'credentials';

const PhoneLoginForm = ({ isOpen, onOpenChange }: PhoneLoginFormProps) => {
    const navigate = useNavigate();
    const { login } = useAuthStore();

    const [step, setStep] = useState<Step>('phone');
    const [isLoading, setIsLoading] = useState(false);
    const [isNewUser, setIsNewUser] = useState(false);

    // Form data
    const [phoneNumber, setPhoneNumber] = useState('');
    const [countryCode, setCountryCode] = useState('91');
    const [countryIso, setCountryIso] = useState('IN');
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [fullName, setFullName] = useState('');
    const [username, setUsername] = useState('');

    // Username validation
    const [isCheckingUsername, setIsCheckingUsername] = useState(false);
    const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
    const [usernameError, setUsernameError] = useState('');

    // OTP
    const [countdown, setCountdown] = useState(0);
    const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Countdown timer
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    // Username availability check with debounce
    useEffect(() => {
        if (!username || username.length < 3) {
            setUsernameAvailable(null);
            setUsernameError('');
            return;
        }

        const timer = setTimeout(async () => {
            setIsCheckingUsername(true);
            try {
                const response = await authService.checkUsername(username);
                setUsernameAvailable(response.available);
                setUsernameError(response.available ? '' : response.message);
            } catch {
                setUsernameError('Failed to check username');
            } finally {
                setIsCheckingUsername(false);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [username]);

    const handleCountryChange = (dialCode: string, isoCode: string) => {
        setCountryCode(dialCode);
        setCountryIso(isoCode);
    };

    const handlePhoneSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!phoneNumber || phoneNumber.length < 10) {
            toast.error('Please enter a valid phone number');
            return;
        }

        setIsLoading(true);
        try {
            const response = await authService.sendPhoneOTP(phoneNumber, countryCode);
            if (response.success) {
                setIsNewUser(response.is_new_user);
                setCountdown(response.timeout || 60);
                setStep('otp');
                toast.success('OTP sent to your phone');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to send OTP');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value.slice(-1);
        setOtp(newOtp);

        if (value && index < 5) {
            otpInputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpInputRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        const newOtp = [...otp];
        for (let i = 0; i < pastedData.length; i++) {
            newOtp[i] = pastedData[i];
        }
        setOtp(newOtp);
        if (pastedData.length === 6) {
            otpInputRefs.current[5]?.focus();
        }
    };

    const handleVerifyOtp = async () => {
        const otpCode = otp.join('');
        if (otpCode.length !== 6) {
            toast.error('Please enter the 6-digit OTP');
            return;
        }

        setIsLoading(true);
        try {
            const response = await authService.verifyPhoneOTP(phoneNumber, otpCode);
            if (response.success) {
                if (response.is_new_user) {
                    setStep('name');
                } else {
                    // Existing user - login directly
                    const loginResponse = await authService.phoneLogin(phoneNumber);
                    if (loginResponse.success && loginResponse.user && loginResponse.token) {
                        login(loginResponse.user, loginResponse.token);
                        toast.success('Welcome back!');
                        navigate('/dashboard', { replace: true });
                    }
                }
            }
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Invalid OTP');
        } finally {
            setIsLoading(false);
        }
    };

    const handleNameSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!fullName.trim()) {
            toast.error('Please enter your name');
            return;
        }
        setStep('credentials');
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!username || username.length < 3) {
            toast.error('Username must be at least 3 characters');
            return;
        }

        if (!usernameAvailable) {
            toast.error('Please choose an available username');
            return;
        }

        setIsLoading(true);
        try {
            const referralCode = sessionStorage.getItem('referral_code') || undefined;
            const response = await authService.phoneRegister({
                phone_number: phoneNumber,
                full_name: fullName,
                username: username,
                referral_code: referralCode
            });

            if (response.success && response.user && response.token) {
                sessionStorage.removeItem('referral_code');
                login(response.user, response.token);
                toast.success('Account created successfully!');
                navigate('/dashboard', { replace: true });
            }
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Registration failed');
        } finally {
            setIsLoading(false);
        }
    };

    const resendOtp = async () => {
        setIsLoading(true);
        try {
            const response = await authService.sendPhoneOTP(phoneNumber, countryCode);
            if (response.success) {
                setCountdown(response.timeout || 60);
                toast.success('OTP sent');
            }
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to resend OTP');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        if (step === 'otp') {
            setStep('phone');
            setOtp(['', '', '', '', '', '']);
        } else if (step === 'name') {
            setStep('otp');
        } else if (step === 'credentials') {
            setStep('name');
        } else {
            onOpenChange(false);
            resetForm();
        }
    };

    const resetForm = () => {
        setStep('phone');
        setPhoneNumber('');
        setCountryCode('91');
        setCountryIso('IN');
        setOtp(['', '', '', '', '', '']);
        setFullName('');
        setUsername('');
        setIsNewUser(false);
        setCountdown(0);
    };

    const handleToggle = () => {
        const newState = !isOpen;
        onOpenChange(newState);
        if (!newState) resetForm();
    };

    return (
        <div className="w-full relative flex flex-col items-center">
            <button
                onClick={handleToggle}
                type="button"
                className="w-full h-12 mb-3 rounded-xl border border-white/20 bg-white/5 hover:bg-white/10 text-white/80 hover:text-white transition-all font-sans font-semibold text-sm flex items-center justify-center gap-2.5 active:scale-[0.98]"
            >
                <Phone size={18} className="text-primary-400/70" />
                Continue with Phone
                <ChevronDown size={16} className={`transition-transform duration-300 text-white/60 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="w-full overflow-hidden">
                    {/* Phone Step */}
                    {step === 'phone' && (
                        <form onSubmit={handlePhoneSubmit} className="space-y-4 pb-4 px-1">
                            <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-[0.2em] text-primary-200/50 ml-1 font-sans font-semibold">
                                    Phone Number
                                </label>
                                <div className="flex gap-1.5">
                                    <CountryCodeSelector
                                        value={countryCode}
                                        onChange={handleCountryChange}
                                        disabled={isLoading}
                                    />
                                    <input
                                        type="tel"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 15))}
                                        placeholder="Phone number"
                                        className="flex-1 min-w-0 px-3 h-11 bg-cosmic-950/50 border border-white/20 rounded-xl text-white placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 text-sm font-sans"
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading || phoneNumber.length < 10}
                                className="w-full py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold font-sans rounded-xl hover:shadow-lg hover:shadow-primary-500/20 transition-colors uppercase tracking-[0.2em] text-[10px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.98]"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Sending...
                                    </>
                                ) : 'Send OTP'}
                            </button>
                        </form>
                    )}

                    {/* OTP Step */}
                    {step === 'otp' && (
                        <div className="space-y-4 pb-4 px-1">
                            <button
                                onClick={handleBack}
                                className="flex items-center gap-1 text-primary-300/60 hover:text-primary-200 text-xs transition-colors mb-2"
                            >
                                <ArrowLeft size={14} />
                                Back
                            </button>

                            <div className="text-center mb-4">
                                <p className="text-sm text-white/70">Enter the code sent to</p>
                                <p className="text-primary-300 font-medium text-sm">+{countryCode} {phoneNumber}</p>
                            </div>

                            <div className="flex justify-center gap-1.5 sm:gap-2">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => { otpInputRefs.current[index] = el; }}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                        onPaste={index === 0 ? handleOtpPaste : undefined}
                                        className="w-10 h-11 sm:w-11 sm:h-12 text-center bg-cosmic-950/50 border border-white/20 rounded-lg text-white text-base sm:text-lg font-mono focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-colors"
                                        disabled={isLoading}
                                        autoFocus={index === 0}
                                    />
                                ))}
                            </div>

                            <button
                                onClick={handleVerifyOtp}
                                disabled={isLoading || otp.join('').length !== 6}
                                className="w-full py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold font-sans rounded-xl hover:shadow-lg hover:shadow-primary-500/20 transition-colors uppercase tracking-[0.2em] text-[10px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.98]"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Verifying...
                                    </>
                                ) : 'Verify Code'}
                            </button>

                            <div className="text-center">
                                {countdown > 0 ? (
                                    <p className="text-xs text-white/40">Resend code in {countdown}s</p>
                                ) : (
                                    <button
                                        onClick={resendOtp}
                                        disabled={isLoading}
                                        className="text-xs text-primary-400 hover:text-primary-300 transition-colors disabled:opacity-50"
                                    >
                                        Resend code
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Name Step (New User) */}
                    {step === 'name' && (
                        <form onSubmit={handleNameSubmit} className="space-y-4 pb-4 px-1">
                            <button
                                onClick={handleBack}
                                type="button"
                                className="flex items-center gap-1 text-primary-300/60 hover:text-primary-200 text-xs transition-colors mb-2"
                            >
                                <ArrowLeft size={14} />
                                Back
                            </button>

                            <div className="text-center mb-4">
                                <p className="text-sm text-white/70">Welcome! What should we call you?</p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-[0.2em] text-primary-200/50 ml-1 font-sans font-semibold">
                                    Full Name
                                </label>
                                <div className="relative">
                                    <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" />
                                    <input
                                        type="text"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        placeholder="Enter your name..."
                                        className="w-full pl-11 pr-4 py-3 bg-cosmic-950/50 border border-white/20 rounded-xl text-white placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-colors duration-200 text-sm font-sans"
                                        required
                                        disabled={isLoading}
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading || !fullName.trim()}
                                className="w-full py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold font-sans rounded-xl hover:shadow-lg hover:shadow-primary-500/20 transition-colors uppercase tracking-[0.2em] text-[10px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.98]"
                            >
                                Continue
                            </button>
                        </form>
                    )}

                    {/* Username Step (New User) */}
                    {step === 'credentials' && (
                        <form onSubmit={handleRegister} className="space-y-4 pb-4 px-1">
                            <button
                                onClick={handleBack}
                                type="button"
                                className="flex items-center gap-1 text-primary-300/60 hover:text-primary-200 text-xs transition-colors mb-2"
                            >
                                <ArrowLeft size={14} />
                                Back
                            </button>

                            <div className="text-center mb-4">
                                <p className="text-sm text-white/70">Choose your username</p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] uppercase tracking-[0.2em] text-primary-200/50 ml-1 font-sans font-semibold">
                                    Username
                                </label>
                                <div className="relative">
                                    <AtSign size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" />
                                    <input
                                        type="text"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                                        className="w-full pl-11 pr-11 py-3 bg-cosmic-950/50 border border-white/20 rounded-xl text-white placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-colors duration-200 text-sm font-sans"
                                        placeholder="Choose a username..."
                                        required
                                        disabled={isLoading}
                                        autoFocus
                                    />
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                        {isCheckingUsername && (
                                            <Loader2 size={16} className="text-white/60 animate-spin" />
                                        )}
                                        {!isCheckingUsername && usernameAvailable && (
                                            <Check size={16} className="text-green-400" />
                                        )}
                                    </div>
                                </div>
                                {usernameError && (
                                    <p className="text-xs text-red-400 ml-1">{usernameError}</p>
                                )}
                                {usernameAvailable && !usernameError && (
                                    <p className="text-xs text-green-400 ml-1">Username available!</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading || !usernameAvailable}
                                className="w-full py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold font-sans rounded-xl hover:shadow-lg hover:shadow-primary-500/20 transition-colors uppercase tracking-[0.2em] text-[10px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.98]"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Creating Account...
                                    </>
                                ) : 'Create Account'}
                            </button>
                        </form>
                    )}
                </div>
            )}
        </div>
    );
};

export default PhoneLoginForm;
